import { useCallback, useEffect, useState, type SyntheticEvent } from "react";
import {
  getCallbackFailureLog,
  listCallbackFailureLogs,
  type Application,
  type CallbackFailureLog,
} from "../api";
import { CopyText, ManagementHeading, PageState, TimeValue } from "./ManagementCommon";
import { Button, IconButton } from "./ui/Button";
import { Dialog } from "./ui/Dialog";
import { Icon } from "./ui/Icon";

interface Filters {
  readonly search: string;
  readonly eventType: string;
  readonly errorType: string;
  readonly deliveryStatus: string;
  readonly from?: number;
  readonly to?: number;
}

const emptyFilters: Filters = { search: "", eventType: "", errorType: "", deliveryStatus: "" };
const errorTypes = [
  "NETWORK_ERROR",
  "DNS_ERROR",
  "TLS_ERROR",
  "TIMEOUT",
  "HTTP_ERROR",
  "INVALID_RESPONSE",
  "RESPONSE_TOO_LARGE",
  "BUSINESS_REJECTED",
  "CONCURRENCY_LIMITED",
  "RETRY_EXHAUSTED",
  "INTERNAL_DELIVERY_ERROR",
] as const;

export function CallbackFailureLogsView({ application, applications, onSelect }: {
  readonly application: Application | null;
  readonly applications: readonly Application[];
  readonly onSelect: (application: Application) => void;
}) {
  const [filters, setFilters] = useState<Filters>(emptyFilters);
  const [logs, setLogs] = useState<readonly CallbackFailureLog[]>([]);
  const [cursor, setCursor] = useState<string | undefined>();
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [cursorStack, setCursorStack] = useState<readonly (string | undefined)[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [detailId, setDetailId] = useState<string | null>(null);
  const reload = useCallback(() => { setReloadKey((value) => value + 1); }, []);
  const filtered = Object.values(filters).some((value) => value !== "" && value !== undefined);

  useEffect(() => {
    setLogs([]);
    setCursor(undefined);
    setCursorStack([]);
    setNextCursor(null);
    setFilters(emptyFilters);
  }, [application?.appId]);

  useEffect(() => {
    if (application === null) return;
    let active = true;
    setLoading(true);
    setLoadError(null);
    void listCallbackFailureLogs(application.appId, { ...filters, cursor, limit: 50 })
      .then((result) => {
        if (active) {
          setLogs(result.logs);
          setNextCursor(result.nextCursor);
        }
      })
      .catch((cause: unknown) => { if (active) setLoadError(errorMessage(cause)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [application?.appId, cursor, filters, reloadKey]);

  function submit(event: SyntheticEvent<HTMLFormElement, SubmitEvent>): void {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    setCursor(undefined);
    setCursorStack([]);
    setFilters({
      search: formString(data, "search").trim(),
      eventType: formString(data, "eventType"),
      errorType: formString(data, "errorType"),
      deliveryStatus: formString(data, "deliveryStatus"),
      ...dateRange(data, "from", "to"),
    });
  }

  function reset(): void {
    setFilters(emptyFilters);
    setCursor(undefined);
    setCursorStack([]);
  }

  return (
    <div className="management-page callback-log-page">
      <ManagementHeading
        title="回调错误日志"
        description="仅保存失败、业务拒绝、重试和死信记录；成功调用只进入聚合指标。"
        applications={applications}
        selected={application}
        onSelect={onSelect}
      />
      {application === null ? (
        <div className="empty-page"><strong>请先创建应用</strong><p>错误日志按应用严格隔离。</p></div>
      ) : (
        <section className="data-card management-card callback-log-card">
          <header className="data-card-header management-toolbar callback-log-header">
            <div>
              <h3>失败记录</h3>
              <p>诊断回调拒绝、网络异常和重试状态</p>
            </div>
            <div className="callback-log-header-actions">
              <span className="management-tag tag-muted">默认保留 30 天</span>
              <Button leadingIcon="activity" size="small" variant="ghost" onClick={reload}>刷新</Button>
            </div>
          </header>

          <form className="callback-log-filters" onSubmit={submit}>
            <div className="callback-filter-grid">
              <label className="callback-filter-field callback-filter-search">
                <span>关键字</span>
                <span className="search-control">
                  <Icon name="search" size={17} />
                  <input name="search" maxLength={256} placeholder="Event、Request、Message、Conversation 或 User ID" />
                </span>
              </label>
              <label className="callback-filter-field">
                <span>回调类型</span>
                <select name="eventType">
                  <option value="">全部回调类型</option>
                  <option value="message.before_send">消息发送前</option>
                  <option value="message.after_send">消息发送后</option>
                  <option value="user.online">用户上线</option>
                  <option value="user.offline">用户下线</option>
                </select>
              </label>
              <label className="callback-filter-field">
                <span>错误类型</span>
                <select name="errorType">
                  <option value="">全部错误类型</option>
                  {errorTypes.map((value) => <option key={value}>{value}</option>)}
                </select>
              </label>
              <label className="callback-filter-field">
                <span>投递状态</span>
                <select name="deliveryStatus">
                  <option value="">全部状态</option>
                  <option>RETRYING</option>
                  <option>RECOVERED</option>
                  <option>DEAD_LETTER</option>
                  <option>BLOCKED</option>
                </select>
              </label>
            </div>
            <div className="callback-filter-footer">
              <div className="callback-date-range" aria-label="发生时间范围">
                <label className="callback-filter-field"><span>开始时间</span><input name="from" type="datetime-local" /></label>
                <span className="callback-date-separator">至</span>
                <label className="callback-filter-field"><span>结束时间</span><input name="to" type="datetime-local" /></label>
              </div>
              <div className="callback-filter-actions">
                <Button size="small" type="reset" variant="ghost" onClick={reset}>重置</Button>
                <Button leadingIcon="search" size="small" type="submit" variant="primary">查询</Button>
              </div>
            </div>
          </form>

          <PageState
            loading={loading}
            error={loadError}
            empty={logs.length === 0}
            filtered={filtered}
            noun="回调错误日志"
            onRetry={reload}
          >
            <div className="table-container">
              <table className="management-table callback-log-table">
                <thead>
                  <tr>
                    <th>发生时间</th>
                    <th>回调类型 / Event ID</th>
                    <th>关联对象</th>
                    <th>回调地址</th>
                    <th>错误摘要</th>
                    <th>HTTP / 耗时</th>
                    <th>重试</th>
                    <th>状态</th>
                    <th><span className="visually-hidden">操作</span></th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => {
                    const relatedId = log.messageId ?? log.conversationId ?? log.userId ?? log.requestId;
                    return (
                      <tr key={log.id}>
                        <td><TimeValue value={log.lastFailedAt} /></td>
                        <td>
                          <span className="callback-table-stack">
                            <span className="management-tag tag-info">{callbackTypeLabel(log.eventType)}</span>
                            <CopyText label="Event ID" value={log.eventId} />
                          </span>
                        </td>
                        <td><code className="callback-related-id" title={relatedId}>{relatedId}</code></td>
                        <td><span className="summary-value" title={log.callbackUrl}>{log.callbackUrl}</span></td>
                        <td>
                          <span className="callback-table-stack">
                            <strong title={log.errorType}>{log.errorType}</strong>
                            <small title={log.errorMessage}>{log.errorMessage}</small>
                          </span>
                        </td>
                        <td>
                          <span className="callback-table-stack callback-table-numeric">
                            <strong>{log.httpStatus ?? "—"}</strong><small>{log.durationMs} ms</small>
                          </span>
                        </td>
                        <td>
                          <span className="callback-table-stack">
                            <strong>第 {log.deliveryAttempt} 次</strong>
                            {log.nextRetryAt === null ? <small>无后续重试</small> : <small>下次 <TimeValue value={log.nextRetryAt} /></small>}
                          </span>
                        </td>
                        <td><FailureStatus status={log.deliveryStatus} /></td>
                        <td><IconButton icon="chevron-right" label="查看错误详情" onClick={() => { setDetailId(log.id); }} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </PageState>

          <footer className="management-pagination">
            <span>当前页 {logs.length} 条{filtered ? " · 已应用筛选" : ""}</span>
            <div>
              <IconButton
                disabled={cursorStack.length === 0}
                icon="chevron-left"
                label="上一页"
                onClick={() => {
                  const previous = cursorStack.at(-1);
                  setCursorStack((items) => items.slice(0, -1));
                  setCursor(previous);
                }}
              />
              <IconButton
                disabled={nextCursor === null}
                icon="chevron-right"
                label="下一页"
                onClick={() => {
                  if (nextCursor !== null) {
                    setCursorStack((items) => [...items, cursor]);
                    setCursor(nextCursor);
                  }
                }}
              />
            </div>
          </footer>
        </section>
      )}
      {detailId === null || application === null ? null : (
        <CallbackLogDetail
          appId={application.appId}
          logId={detailId}
          onClose={() => { setDetailId(null); }}
        />
      )}
    </div>
  );
}

function CallbackLogDetail({ appId, logId, onClose }: {
  readonly appId: string;
  readonly logId: string;
  readonly onClose: () => void;
}) {
  const [log, setLog] = useState<CallbackFailureLog | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void getCallbackFailureLog(appId, logId)
      .then((value) => { if (active) setLog(value); })
      .catch((cause: unknown) => { if (active) setError(errorMessage(cause)); });
    return () => { active = false; };
  }, [appId, logId]);

  return (
    <Dialog title="回调错误详情" description="仅展示经过脱敏和截断的诊断信息" size="large" onClose={onClose}>
      <div className="dialog-body callback-log-detail">
        {error !== null ? (
          <div className="management-state error-state"><strong>详情加载失败</strong><p>{error}</p></div>
        ) : log === null ? (
          <div className="management-state"><span className="loader" /><strong>正在加载详情</strong></div>
        ) : (
          <>
            <dl>
              <Detail label="Event ID" value={log.eventId} copy />
              <Detail label="Request ID" value={log.requestId} copy />
              <Detail label="事件类型" value={log.eventType} />
              <Detail label="投递状态" value={log.deliveryStatus} />
              <Detail label="错误类型" value={log.errorType} />
              <Detail label="错误码" value={log.errorCode} />
              <Detail label="HTTP 状态" value={String(log.httpStatus ?? "—")} />
              <Detail label="耗时" value={`${log.durationMs} ms`} />
              <Detail label="投递次数" value={String(log.deliveryAttempt)} />
              <Detail label="回调地址" value={log.callbackUrl} />
            </dl>
            <section><h3>错误摘要</h3><p>{log.errorMessage}</p>{log.responseSummary === null ? null : <pre>{log.responseSummary}</pre>}</section>
          </>
        )}
      </div>
      <footer className="dialog-footer"><Button onClick={onClose}>关闭</Button></footer>
    </Dialog>
  );
}

function Detail({ label, value, copy = false }: {
  readonly label: string;
  readonly value: string;
  readonly copy?: boolean;
}) {
  return <div><dt>{label}</dt><dd>{copy ? <CopyText label={label} value={value} /> : value}</dd></div>;
}

function FailureStatus({ status }: { readonly status: CallbackFailureLog["deliveryStatus"] }) {
  const config = {
    RETRYING: { label: "重试中", className: "tag-info" },
    RECOVERED: { label: "已恢复", className: "tag-success" },
    DEAD_LETTER: { label: "死信", className: "tag-danger" },
    BLOCKED: { label: "已阻断", className: "tag-danger" },
  }[status];
  return <span className={`management-tag ${config.className}`} title={status}>{config.label}</span>;
}

function callbackTypeLabel(value: string): string {
  return {
    "message.before_send": "消息发送前",
    "message.after_send": "消息发送后",
    "user.online": "用户上线",
    "user.offline": "用户下线",
  }[value] ?? value;
}

function dateRange(data: FormData, fromName: string, toName: string): {
  readonly from?: number;
  readonly to?: number;
} {
  const from = Date.parse(formString(data, fromName));
  const to = Date.parse(formString(data, toName));
  return {
    ...(Number.isFinite(from) ? { from } : {}),
    ...(Number.isFinite(to) ? { to } : {}),
  };
}

function formString(data: FormData, name: string): string {
  const value = data.get(name);
  return typeof value === "string" ? value : "";
}

function errorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}
